import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { getProgressArchiveStatus, getSemesters } from "../lib/api";

/** Builds the current semester context. Uses the school's CONFIGURED semester
 * end-date (from Settings ▸ Semesters) when available, falling back to defaults.
 * The reminder only shows AFTER the configured end date (through 30 days after).
 */
function buildContext(today, semesters) {
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-11
  let semester, school_year, semId, end;
  if (m >= 7) {
    semester = `Fall-${y}`;
    school_year = `${y}-${y + 1}`;
    semId = "fall";
    end = new Date(y, 11, 20); // default Dec 20
  } else {
    semester = `Spring-${y}`;
    school_year = `${y - 1}-${y}`;
    semId = "spring";
    end = new Date(y, 5, 15); // default Jun 15
  }
  // Prefer the admin-configured end date for this semester
  const cfg = (semesters || []).find(
    (s) => ((s.id || "").toLowerCase().includes(semId) || (s.name || "").toLowerCase().includes(semId)) && s.end_date
  );
  if (cfg && cfg.end_date) {
    const parsed = new Date(`${cfg.end_date}T23:59:59`);
    if (!isNaN(parsed.getTime())) end = parsed;
  }
  const days_to_end = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  return { semester, school_year, end, days_to_end };
}

export default function ProgressArchiveReminder() {
  const [status, setStatus] = useState(null);
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Determine the current semester/school-year first, then fetch THAT year's
    // configured dates (not the most-recent year, which may be blank).
    const base = buildContext(new Date(), []);
    getSemesters(base.school_year)
      .then((res) => {
        const sems = res.data?.semesters || [];
        if (!cancelled) setCtx(buildContext(new Date(), sems));
      })
      .catch(() => { if (!cancelled) setCtx(base); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;
    getProgressArchiveStatus(ctx.semester, ctx.school_year)
      .then((res) => { if (!cancelled) setStatus(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ctx]);

  if (!ctx) return null;
  if (!status) return null;
  // Don't show if everything is already archived
  if (status.missing_count === 0 && status.archived_count > 0) return null;

  return (
    <Card className="border-l-4 border-amber-400 bg-amber-50/40" data-testid="archive-reminder-card">
      <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-500 hover:bg-amber-500">End of Semester</Badge>
            <span className="text-sm font-semibold">Archive progress reports for {ctx.semester.replace("-", " ")}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            <strong>{status.missing_count}</strong> of <strong>{status.total_active_students}</strong> students still need a Progress Report archived to their student file for this semester.
          </p>
        </div>
        <Button asChild className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="archive-reminder-cta">
          <Link to="/progress-tracking?tab=reports">Archive Progress Reports →</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
